"use client";

import Link from "next/link";
import Image from "next/image";
import { getCalculator } from "@/lib/calculators-data";
import { CALCULATOR_RELATIONS } from "@/lib/calculator-relations-data";
import { articles } from "@/lib/datasets/articles";
import { getArticleImage } from "@/lib/image-mapper";

interface RelatedCalculatorsProps {
  calculatorId: string;
}

// Maps calculator IDs to related article slugs
const CALCULATOR_GUIDES_MAP: Record<string, string[]> = {
  concrete: ["how-much-concrete-do-i-need-guide", "driveway-sealing-cost-guide", "basement-waterproofing-cost-guide"],
  paint: ["how-much-paint-do-you-need-per-room", "cost-to-paint-house-exterior"],
  flooring: ["vinyl-vs-laminate-vs-hardwood-flooring", "flooring-installation-cost-per-square-foot"],
  deck: ["cost-to-build-a-deck-guide", "best-time-for-renovations"],
  tile: ["vinyl-vs-laminate-vs-hardwood-flooring", "flooring-installation-cost-per-square-foot"],
  roofing: ["roof-lifespan-by-material", "10-signs-you-need-a-new-roof", "does-homeowners-insurance-cover-roof-replacement", "how-to-photograph-roof-for-insurance-claim", "gutter-replacement-cost-guide"],
  hvac: ["hvac-replacement-cost-guide", "signs-your-ac-is-dying-repair-vs-replace", "heat-pump-vs-central-ac-comparison", "will-new-ac-lower-electric-bill", "seer-rating-explained-ac-bill"],
  solar: ["solar-panel-costs", "solar-panel-payback-period-explained", "is-home-energy-audit-worth-it"],
  windows: ["window-replacement-cost-guide", "impact-windows-vs-hurricane-shutters-cost", "2026-hurricane-season-home-prep"],
  bathroom: ["bathroom-remodel-cost-guide", "renovations-add-resale-value", "how-long-does-a-water-heater-last"],
  kitchen: ["kitchen-remodel-cost-guide", "renovations-add-resale-value", "best-time-for-renovations"],
  roi: ["renovations-add-resale-value", "pre-listing-home-inspection-checklist", "best-time-for-renovations"],
  "water-heater": ["how-long-does-a-water-heater-last", "is-home-energy-audit-worth-it", "will-new-ac-lower-electric-bill"],
  "roof-shingles": ["roof-lifespan-by-material", "10-signs-you-need-a-new-roof", "does-homeowners-insurance-cover-roof-replacement", "gutter-replacement-cost-guide"],
  siding: ["siding-replacement-cost-guide", "cost-to-paint-house-exterior", "window-replacement-cost-guide"],
  driveway: ["driveway-sealing-cost-guide", "how-much-concrete-do-i-need-guide", "cost-to-sod-or-reseed-lawn"],
  "home-addition": ["renovations-add-resale-value", "kitchen-remodel-cost-guide", "bathroom-remodel-cost-guide", "best-time-for-renovations"],
  fence: ["wood-vs-vinyl-vs-metal-fence-comparison", "cost-to-sod-or-reseed-lawn"],
  drywall: ["how-much-paint-do-you-need-per-room"],
  mulch: ["cost-to-sod-or-reseed-lawn", "driveway-sealing-cost-guide"],
  area: ["how-much-concrete-do-i-need-guide", "how-much-paint-do-you-need-per-room"],
};

export default function RelatedCalculators({ calculatorId }: RelatedCalculatorsProps) {
  const relations = CALCULATOR_RELATIONS[calculatorId];
  
  // Find related articles for this calculator
  const relatedSlugs = CALCULATOR_GUIDES_MAP[calculatorId] || [];
  const relatedArticles = articles.filter((art) => relatedSlugs.includes(art.slug));

  const hasCalculators = relations && relations.length > 0;
  const hasArticles = relatedArticles.length > 0;

  if (!hasCalculators && !hasArticles) return null;

  return (
    <div className="space-y-16 mt-16 w-full">
      {/* ── Related Calculators Section ─────────────────────── */}
      {hasCalculators && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-extrabold uppercase tracking-wider text-slate-900 dark:text-white font-heading-condensed whitespace-nowrap">
              Related Projects & Standards
            </h3>
            <div className="flex-1 h-px bg-[var(--line-strong)]" />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium -mt-4">
            Explore adjacent calculations and the construction codes linking these tasks on the job site.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
            {relations.map((rel, idx) => {
              const target = getCalculator(rel.relatedId);
              if (!target) return null;

              return (
                <div
                  key={idx}
                  className="group flex flex-col p-6 rounded-lg border border-[var(--line)] bg-[var(--surface)] hover:border-[var(--accent)] transition-all duration-150 h-full text-left"
                >
                  <div className="mb-2">
                    <span className="text-[8px] font-mono font-bold tracking-widest text-[var(--rust)] uppercase">
                      {target.category === "diy" ? "Material Estimator" : "Cost Estimator"}
                    </span>
                  </div>

                  <h4 className="text-base font-extrabold text-[var(--ink)] mb-1.5 font-sans">
                    {target.name}
                  </h4>
                  
                  <p className="text-[var(--ink-soft)] text-xs leading-relaxed mb-6 font-sans flex-1">
                    {target.description}
                  </p>

                  <Link
                    href={`/calculators/${target.slug}/`}
                    className="w-full text-center text-xs font-bold py-2.5 rounded-lg bg-[var(--surface-muted)] hover:bg-[var(--line)] text-[var(--ink)] transition-colors duration-150 block mt-auto"
                  >
                    Open Calculator
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Related Guides & Articles Section ───────────────── */}
      {hasArticles && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-extrabold uppercase tracking-wider text-slate-900 dark:text-white font-heading-condensed whitespace-nowrap">
              Related Guides &amp; Project Advice
            </h3>
            <div className="flex-1 h-px bg-[var(--line-strong)]" />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium -mt-4">
            Read our trade-tested analysis, choosing building materials, and budgeting tips related to this calculation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
            {relatedArticles.map((article) => {
              const articleImage = getArticleImage(article.slug);
              return (
                <Link
                  key={article.slug}
                  href={`/articles/${article.slug}/`}
                  className="group flex flex-col h-full bg-[var(--surface)] border border-[var(--border)] rounded-lg p-5 card-blueprint hover:border-[var(--accent)] transition-all duration-150 relative text-left"
                >
                  {/* Header Image */}
                  <div className="relative w-full aspect-[16/10] rounded overflow-hidden mb-3.5 bg-[var(--surface-muted)] border border-[var(--border)]">
                    <Image
                      src={articleImage}
                      alt={article.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 360px"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mb-2 text-[8px] font-mono uppercase tracking-widest text-[var(--ink-faint)]">
                    <span className="font-bold text-[var(--accent)]">
                      {article.category}
                    </span>
                    <span>•</span>
                    <span>{article.updatedAt || article.createdAt}</span>
                  </div>
                  <h4 className="text-sm font-extrabold text-[var(--ink)] group-hover:text-[var(--accent)] tracking-tight mb-2 line-clamp-2 font-sans transition-colors uppercase leading-snug">
                    {article.title}
                  </h4>
                  <p className="text-[var(--ink-soft)] text-xs leading-relaxed line-clamp-2 font-sans flex-1">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--line)] text-[9px] font-mono text-[var(--ink-faint)] uppercase tracking-wider">
                    <span>{article.readTime}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="group-hover:translate-x-0.5 transition-transform text-[var(--ink-faint)] group-hover:text-[var(--accent)]">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
